import { httpClient, authTokenUtils } from '@/api/httpClient';
import { deviceIdentity } from '@/api/deviceIdentity';
import type {
  AuthSession,
  BackendLoginResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  RegisterOrganizationAdminRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  User
} from '@/types/auth';

export const authApi = {
  /**
   * Public self-service sign-up for a new organization + its admin user. The
   * caller is unauthenticated (`skipAuth`); we don't depend on the response
   * shape — success simply means the account was created, and the user then
   * signs in normally.
   */
  registerOrganizationAdmin(payload: RegisterOrganizationAdminRequest) {
    return httpClient<User>('/api/v1/auth/register/organization-admin/', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuth: true
    });
  },
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await httpClient<BackendLoginResponse>('/api/v1/auth/login/', {
      method: 'POST',
      body: JSON.stringify({
        ...credentials,
        device_id: credentials.device_id ?? deviceIdentity.getDeviceId(),
        device_name: credentials.device_name ?? deviceIdentity.getDeviceName()
      }),
      skipAuth: true
    });
    const tokens = authTokenUtils.normalizeTokens(response);

    if (!tokens.accessToken || !tokens.refreshToken) {
      throw new Error('Invalid login response');
    }

    return {
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
      user: response.user
    };
  },
  /**
   * Authenticated password change. The backend revokes every session and returns
   * a fresh token pair for this device (same shape as login). Callers MUST persist
   * the returned tokens via `authTokenUtils.tokenStorage.setTokens`, or the next
   * refresh fails against the now-revoked session.
   */
  async changePassword(payload: ChangePasswordRequest): Promise<LoginResponse> {
    const response = await httpClient<BackendLoginResponse>('/api/v1/auth/me/password/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    const tokens = authTokenUtils.normalizeTokens(response);

    if (!tokens.accessToken || !tokens.refreshToken) {
      throw new Error('Invalid change-password response');
    }

    return {
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
      user: response.user
    };
  },
  /**
   * Public "forgot my password" request. Always resolves 202 with no body,
   * whether or not the account exists (anti-enumeration); the caller must never
   * infer existence from the outcome.
   */
  forgotPassword(payload: ForgotPasswordRequest) {
    return httpClient<void>('/api/v1/auth/password/forgot/', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuth: true,
      skipRefresh: true
    });
  },
  /**
   * Public reset completion from the emailed link. Resolves 204 (no tokens); the
   * user then signs in normally. An invalid/expired/used token yields a 400 with
   * `ApiError.code === 'invalid_reset_token'`.
   */
  resetPassword(payload: ResetPasswordRequest) {
    return httpClient<void>('/api/v1/auth/password/reset/', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuth: true,
      skipRefresh: true
    });
  },
  me() {
    return httpClient<User>('/api/v1/auth/me/');
  },
  updateMe(payload: UpdateProfileRequest) {
    return httpClient<User>('/api/v1/auth/me/', {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  },
  /**
   * Registers the caller's signature specimen — the one printed on the "personal
   * autorizado" sheet of the official PDFs. Multipart, so it gets its own endpoint
   * instead of riding on the JSON `PATCH /auth/me/`.
   */
  uploadMySignature(signature: File) {
    const body = new FormData();
    body.append('registered_signature', signature);

    return httpClient<User>('/api/v1/auth/me/signature/', {
      method: 'POST',
      body
    });
  },
  deleteMySignature() {
    return httpClient<User>('/api/v1/auth/me/signature/', {
      method: 'DELETE'
    });
  },
  logout() {
    const refreshToken = authTokenUtils.tokenStorage.getRefreshToken();

    return httpClient<void>('/api/v1/auth/logout/', {
      method: 'POST',
      body: JSON.stringify({ refresh: refreshToken ?? '' }),
      skipAuth: true,
      skipRefresh: true
    });
  },
  listSessions() {
    return httpClient<AuthSession[]>('/api/v1/auth/sessions/');
  },
  revokeSession(id: string) {
    return httpClient<void>(`/api/v1/auth/sessions/${id}/`, {
      method: 'DELETE'
    });
  },
  revokeAllSessions() {
    return httpClient<void>('/api/v1/auth/sessions/revoke-all/', {
      method: 'POST'
    });
  }
};
